'use client';

import { Button } from '@repo/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/design-system/components/ui/card';
import { Input } from '@repo/design-system/components/ui/input';
import { Label } from '@repo/design-system/components/ui/label';
import { useToast } from '@repo/design-system/hooks/use-toast';
import { cn } from '@repo/design-system/lib/utils';

import { signIn } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { readUnverifiedSigninCode } from '../../utils/unverified-account';
import { goToVerification } from './unverified-account';

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const [toastShown, setToastShown] = useState(false);

  // Get email from URL parameters if available
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
    // Show toast for 2fa_enabled or session_revoked
    const message = searchParams.get('message');
    if (!toastShown && message) {
      if (message === '2fa_enabled') {
        toast({
          title: 'Two-Factor Authentication Enabled',
          description:
            'You have been logged out. Please sign in again with your new 2FA settings.',
        });
        setToastShown(true);
      } else if (message === 'session_revoked') {
        toast({
          title: 'Session Revoked',
          description: 'Your session was revoked. Please sign in again.',
        });
        setToastShown(true);
      }
    }
  }, [searchParams, toast, toastShown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    // Get device info from browser
    const getDeviceInfo = () => {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return null;
      }

      const userAgent = navigator.userAgent;
      let deviceName = 'Unknown Device';

      // Enhanced device detection
      if (userAgent.includes('iPhone')) {
        deviceName = 'iPhone - Safari';
      } else if (userAgent.includes('iPad')) {
        deviceName = 'iPad - Safari';
      } else if (
        userAgent.includes('Android') &&
        userAgent.includes('Mobile')
      ) {
        if (userAgent.includes('Chrome')) {
          deviceName = 'Android Phone - Chrome';
        } else {
          deviceName = 'Android Phone';
        }
      } else if (userAgent.includes('Android')) {
        deviceName = 'Android Tablet';
      } else if (userAgent.includes('Windows')) {
        if (userAgent.includes('Chrome')) {
          deviceName = 'Windows PC - Chrome';
        } else if (userAgent.includes('Firefox')) {
          deviceName = 'Windows PC - Firefox';
        } else if (userAgent.includes('Edge')) {
          deviceName = 'Windows PC - Edge';
        } else {
          deviceName = 'Windows PC';
        }
      } else if (
        userAgent.includes('Macintosh') ||
        userAgent.includes('Mac OS X')
      ) {
        if (userAgent.includes('Chrome')) {
          deviceName = 'MacBook - Chrome';
        } else if (
          userAgent.includes('Safari') &&
          !userAgent.includes('Chrome')
        ) {
          deviceName = 'MacBook - Safari';
        } else if (userAgent.includes('Firefox')) {
          deviceName = 'MacBook - Firefox';
        } else {
          deviceName = 'MacBook';
        }
      } else if (userAgent.includes('Linux')) {
        deviceName = 'Linux PC';
      }

      return {
        device_name: deviceName,
        user_agent: userAgent,
      };
    };

    const deviceInfo = getDeviceInfo();

    try {
      const result = await signIn('credentials', {
        email: formData.get('email') as string,
        password: formData.get('password') as string,
        device_name: deviceInfo?.device_name || '',
        user_agent: deviceInfo?.user_agent || '',
        redirect: false,
      });

      const unverified = readUnverifiedSigninCode(result?.code);
      if (unverified) {
        // The account exists; its email was never verified. Not a wrong
        // password, and "Authentication failed." left people stuck here.
        goToVerification(formData.get('email') as string, {
          reason: 'unverified',
          codeSent: unverified.codeSent,
        });
        return;
      }
      if (result?.error) {
        // next-auth folds every other authorize() failure into a bare
        // CredentialsSignin, so there is no better message to show.
        setError('Authentication failed.');
      } else if (result?.ok) {
        // Same-origin paths only. This value lands in window.location, so an
        // absolute or protocol-relative URL here would let a crafted signin
        // link bounce a freshly authenticated user off-site.
        const requested = searchParams.get('callbackUrl');
        const safe =
          requested && requested.startsWith('/') && !requested.startsWith('//')
            ? requested
            : null;
        window.location.href = safe || '/overview';
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Sign-in error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>
            Login with your username and password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <div className="grid gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="m@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center">
                    <Label htmlFor="password">Password</Label>
                    <a
                      href="/forgot-password"
                      className="ml-auto text-sm underline-offset-4 hover:underline"
                    >
                      Forgot your password?
                    </a>
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                  />
                </div>
                {error && <div className="text-red-500 text-sm">{error}</div>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Login'}
                </Button>
              </div>
              <div className="text-center text-sm">
                Don&apos;t have an account?{' '}
                <a href="/signup" className="underline underline-offset-4">
                  Sign up
                </a>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-muted-foreground text-xs [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary">
        By clicking continue, you agree to our{' '}
        <a href="https://www.1099policy.com/terms/user">Terms of Service</a> and{' '}
        <a href="https://www.1099policy.com/legal/privacy">Privacy Policy</a>.
      </div>
    </div>
  );
}
